import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEventLogging } from '../../../../models/Logging';
import { getDefaultDateStringValue } from '../../../../models/UiUtils';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import { Button } from '../../../components-v2/Button';
import { THEME } from '../../../components-v2/Theme/theme';
import { DeleteConfirmationPopover } from '../../../components/DeleteConfirmationPopover';
import { InlineValueEditor } from '../../../components/entities/InlineValueEditor';
import { KeyFactWithKeyDate } from '../../../components/entities/KeyFactWithKeyDate';
import { EditItemPenIcon } from '../../../components/svgs/icons/EditItemPenIcon';
import { LightBulbIcon } from '../../../components/svgs/icons/LightBulbIcon';
import { PrivateVisibilityIcon } from '../../../components/svgs/icons/PrivateVisibilityIcon';
import { TrashIcon } from '../../../components/svgs/icons/TrashIcon';
import { SimpleTooltip, TooltipProvider } from '../../../components/Tooltip';
import { grayIconFill } from '../../../styles/colors';
import { bs } from '../../../styles/styles';
import { styleSheet } from './styles';

interface Props {
	compactLayout: boolean;
	companyVm: Api.CompanyViewModel;
}

// Facts pulled from an integration are owned by that system; only self-created and CSV-imported facts
// can be edited here.
const canEditKeyFact = (keyFact: Api.IKeyFact) =>
	!keyFact.source || keyFact.source?.source === Api.IntegrationSources.CsvImport;

function _CompanyInfoKeyFacts({ compactLayout, companyVm }: Props) {
	const [keyFacts, setKeyFacts] = React.useState<Api.IKeyFact[] | null>(null);
	const errorMessages = useErrorMessages();
	const { logEvent } = useEventLogging('CompanyInfoKeyFacts');
	const [editableKeyFact, setEditableKeyFact] = React.useState<Api.IKeyFact | null>(null);
	const [editItemIndex, setEditItemIndex] = React.useState<number>(-1);
	const [showEditNewItemInput, setShowEditNewItemInput] = React.useState(false);
	const [itemToBeDeletedIndex, setItemToBeDeletedIndex] = React.useState<number | null>(null);
	const onAddKeyFact = (keyFact: Api.IKeyFact) => {
		const newKeyFacts = [...companyVm.keyFactsCollection];
		newKeyFacts.push(keyFact);
		const promise = companyVm.updateKeyFactsCollection(newKeyFacts);
		if (promise) {
			logEvent('AddKeyFact');
			setKeyFacts(newKeyFacts);
			promise
				.then(() => {
					setKeyFacts(null);
				})
				.catch((error: Api.IOperationResultNoValue) => {
					setKeyFacts(null);
					logEvent('AddKeyFact-Error', { ...error });
					errorMessages.pushApiError(error);
				});
		}
	};
	const onUpdateKeyFact = (keyFact: Api.IKeyFact) => {
		const newKeyFacts = [...companyVm.keyFactsCollection];
		// if the key fact doesn't have a value, this actually just removes it from the collection
		const index = companyVm.keyFactsCollection.findIndex(x => x.id === keyFact.id);
		if (keyFact.value) {
			newKeyFacts.splice(index, 1, keyFact);
		} else {
			newKeyFacts.splice(index, 1);
		}
		const promise = companyVm.updateKeyFactsCollection(newKeyFacts);
		if (promise) {
			logEvent('UpdateKeyFact', { id: keyFact.id });
			setKeyFacts(newKeyFacts);
			promise
				.then(() => {
					setKeyFacts(null);
				})
				.catch((error: Api.IOperationResultNoValue) => {
					setKeyFacts(null);
					logEvent('UpdateKeyFact-Error', { ...error });
					errorMessages.pushApiError(error);
				});
		}
	};
	const onAddNewButtonClicked = () => {
		// need to init with null properties
		setEditableKeyFact({
			id: null,
			value: null,
		});
		setEditItemIndex(-1);
		setShowEditNewItemInput(true);
	};
	const onEditCanceled = () => {
		setEditableKeyFact(null);
		setEditItemIndex(-1);
		setShowEditNewItemInput(false);
	};
	const onEditTextChanged = (value: string) => {
		if (editableKeyFact) {
			setEditableKeyFact({
				...editableKeyFact,
				value,
			});
		}
	};
	const onItemEditButtonClicked = (item: Api.IKeyFact, index: number) => {
		setEditableKeyFact({
			id: null,
			value: '',
			...(item || {}),
		});
		setEditItemIndex(index);
		setShowEditNewItemInput(false);
	};
	const onRemoveItem = (item: Api.IKeyFact) => {
		if (item.id) {
			onUpdateKeyFact({
				...item,
				value: null,
			});
		}
		setEditableKeyFact(null);
		setEditItemIndex(-1);
		setShowEditNewItemInput(false);
	};
	const onSaveEdit = () => {
		if (editableKeyFact) {
			setEditableKeyFact({
				...editableKeyFact,
				value: editableKeyFact.value?.trim() || '',
			});
			if (editableKeyFact.id) {
				onUpdateKeyFact(editableKeyFact);
			} else {
				onAddKeyFact(editableKeyFact);
			}
		}
		setEditableKeyFact(null);
		setEditItemIndex(-1);
		setShowEditNewItemInput(false);
	};
	const renderValueEditor = () => (
		<InlineValueEditor
			autoComplete='off'
			autoFocus={true}
			compactLayout={compactLayout}
			disabled={!!companyVm.isBusy}
			id='entity-info-bullet-list-editor-input'
			onCancelButtonClicked={onEditCanceled}
			onChange={ev => onEditTextChanged(ev.target.value)}
			onKeyDown={ev => {
				if (ev.keyCode === 13) {
					onSaveEdit();
				}
			}}
			onSaveButtonClicked={onSaveEdit}
			value={editableKeyFact?.value || ''}
		/>
	);
	const items = keyFacts || companyVm.keyFactsCollection || [];
	return (
		<TooltipProvider>
			<div className={css(styleSheet.keyFactsContainer)}>
				<LightBulbIcon className={css(styleSheet.keyFactsIcon)} fill={THEME.colors.text.light} height={24} width={24} />
				<div className={css(styleSheet.keyFactsListContainer)}>
					<ul className={css(styleSheet.keyFactsList)}>
						{items.map((item, index) => {
							if (editItemIndex === index) {
								return <li key={`keyfact-${item.id}-${index}`}>{renderValueEditor()}</li>;
							}
							const dateString = item.lastModifiedDate || item.creationDate;
							const displayDate = dateString ? getDefaultDateStringValue(dateString) : null;
							const isDeleting = itemToBeDeletedIndex === index;
							return (
								<li className={css(styleSheet.keyFactItem)} key={`keyfact-${item.id}-${index}`}>
									<div className={css(styleSheet.keyFactItemValueContainer)}>
										<div className={css(styleSheet.keyFactItemValue)}>
											{item.keyDate ? <KeyFactWithKeyDate keyFact={item} readonly={!!companyVm.isBusy} /> : item.value}
										</div>
										{!!displayDate && <div className={css(styleSheet.keyFactItemDate)}>{displayDate}</div>}
									</div>
									<div
										className={`${css(styleSheet.keyFactItemActions)} company-key-fact-actions ${
											isDeleting ? 'deleting' : ''
										}`}
									>
										{canEditKeyFact(item) ? (
											<>
												<Button
													aria-label='Edit key fact'
													disabled={!!companyVm.isBusy}
													onClick={() => onItemEditButtonClicked(item, index)}
													size='icon'
													variant='ghost'
												>
													<EditItemPenIcon />
												</Button>
												<DeleteConfirmationPopover
													message='Do you want to delete this key fact?'
													onConfirm={() => onRemoveItem(item)}
													onOpenChange={open => !open && setItemToBeDeletedIndex(null)}
													open={isDeleting}
												>
													<Button
														aria-label='Delete key fact'
														disabled={!!companyVm.isBusy}
														onClick={() => setItemToBeDeletedIndex(index)}
														size='icon'
														variant='ghost'
													>
														<TrashIcon />
													</Button>
												</DeleteConfirmationPopover>
											</>
										) : (
											<SimpleTooltip
												title={
													item.source?.source
														? `This information is from ${item.source.source} and cannot be edited or deleted.`
														: 'This information cannot be edited or deleted.'
												}
											>
												<div>
													<PrivateVisibilityIcon fillColor={grayIconFill} />
												</div>
											</SimpleTooltip>
										)}
									</div>
								</li>
							);
						})}
					</ul>
					<div className={css(bs.flex, bs.itemsCenter, bs.h8, bs.relative)}>
						{showEditNewItemInput ? (
							renderValueEditor()
						) : (
							<button
								className={`${css(bs.px1)} brand-link`}
								disabled={!!companyVm.isBusy || editItemIndex >= 0}
								onClick={onAddNewButtonClicked}
							>
								<span>Add a key fact</span>
							</button>
						)}
					</div>
				</div>
			</div>
		</TooltipProvider>
	);
}

export const CompanyInfoKeyFacts = observer(_CompanyInfoKeyFacts);
